import { User } from "../models/User";
import { MissedAcknowledgment} from "../models/MissedAcknowledgment";
import { Organization } from "../models/Organization";
import { filter, orderBy, groupBy, keys, forEach, find, omit, isEmpty, slice, sortBy } from 'lodash';
import { ACKNOWLEDGEMENT_STATUS } from '../constants';
import { isEqual } from 'date-fns';

export interface IMissedAcknowledgmentReport {
	periods: IMissedAcknowledgmentPeriod[];
}

export interface IMissedAcknowledgmentPeriod {
	startDate?: Date,
	endDate?: Date,
	users: IMissedAcknowledgmentPeriodUser[]
	isCurrent: boolean
}

export interface IMissedAcknowledgmentPeriodUser {
	userID: number,
	firstName: string,
	lastName: string,
	acknowledgments: IMissedAcknowledgment[];
	acknowledgmentHistory: IMissedAcknowledgment[]
}

export interface IMissedAcknowledgment {
	status: ACKNOWLEDGEMENT_STATUS;
}

export function buildGroupDrivers (users?: User[]) : User[] {

	let dashboardUsers = [];

	dashboardUsers = filter(users, u => u.scorecard !== null && u.scorecard.currentScore !== null && u.scorecard.currentScore !== undefined && u.scorecard.currentScore < 90);

	return orderBy(dashboardUsers, user => user.scorecard.currentScore, ['asc']);
}

export function buildGroupDriversWithIncident (users: User[], incidentName: string) : User[] {

	let result: User[] = [];

	forEach(users, user => {

		const userIncidents = filter(user.scorecard.incidents, i => i.name === incidentName)

		if (!isEmpty(userIncidents)) {
			result.push(user);
		}
	})

	return result;
}

export function buildMissedAcknowledgments (organization?: Organization, data?: [MissedAcknowledgment]) : IMissedAcknowledgmentPeriod[] {

	var groupedData = groupBy(data, 'startDate');

	var resultKeys = keys(groupedData);

	const report : IMissedAcknowledgmentReport = { periods: [] }

	if (organization === undefined || data === undefined){
		return report.periods;
	}


	forEach(resultKeys, key => {

	  const val = groupedData[key];
	  
	  var period = find(report.periods, period => period.startDate === key);

	  if (period === undefined) {
	  	period = {startDate: new Date(key), endDate: new Date(val.endDate), users: [], isCurrent: false}
	  }

	  forEach(val, ack => {
	    
	    const user : IMissedAcknowledgmentPeriodUser = {userID: ack.user?.userID, firstName: ack.user?.firstName, lastName: ack.user?.lastName, acknowledgments: [], acknowledgmentHistory: []}
	    period.endDate = new Date(ack.endDate);
	    var existingUser = find(period.users, u => u.userID === user.userID)

	    if (existingUser === undefined) {

	    	period.users.push(user);
	    }
	    	    
	    user.acknowledgments.push(omit(ack, 'user'))
	  })

	  report.periods.push(period);
	})


	// Check if it has a period that matches the org date
	if (organization?.startViewableWindow) {
		const organizationStartDate = new Date(organization.startViewableWindow); 

		const existingPeriod = find(report.periods, p => isEqual(p.startDate, organizationStartDate));

		if (!existingPeriod) {

			const currentPeriodPlaceholder = {startDate: organization?.startViewableWindow ? organizationStartDate : undefined, endDate: organization?.endViewableWindow ? new Date(organization?.endViewableWindow) : undefined, users: [], isCurrent: true }
			report.periods.push(currentPeriodPlaceholder);
		} else {
			existingPeriod.isCurrent = true;
		}	
	}


	report.periods = orderBy(report.periods, 'startDate', ['desc']);
	report.periods = slice(report.periods, 0, 6);

	if (!isEmpty(report.periods)) {

		// Temporary until we sort out the timezone
		// report.periods[0].isCurrent = true;

		const currentPeriod = find(report.periods, p => p.isCurrent === true);
		currentPeriod.users = filter(currentPeriod.users, u => filter(u.acknowledgments, ack => ack.acknowledged === ACKNOWLEDGEMENT_STATUS.NOT_ACKNOWLEDGED).length > 0)
		currentPeriod.users = sortBy(currentPeriod.users, ['firstName', 'lastName']);

		const remainingPeriods = slice(report.periods, 1, 6);

		forEach(currentPeriod.users, u => {

			const ack = find(u.acknowledgments, a => isEqual(new Date(a.startDate), currentPeriod.startDate));

			if (ack) {

				const historicalAckReport : IMissedAcknowledgment = {status: ack.acknowledged}

				u.acknowledgmentHistory.push(historicalAckReport);
			}

			forEach(remainingPeriods, period => {


				const historicalUserReport = find(period.users, periodUser => periodUser.userID === u.userID);

				if (historicalUserReport) {

					const userHistoricalReportAck = find(historicalUserReport.acknowledgments, ack => isEqual(new Date(ack.startDate), period.startDate));

					if (userHistoricalReportAck) {
						const historicalAckReport : IMissedAcknowledgment = {status: userHistoricalReportAck.acknowledged}
						u.acknowledgmentHistory.push(historicalAckReport);
					} else {
						console.error("Did not find historical ack for user id " + u.userID + " and start date + ", period.startDate);
					}
				} else {
					const historicalAckReport : IMissedAcknowledgment = {status: ACKNOWLEDGEMENT_STATUS.ACK_NOT_REQUIRED}
					u.acknowledgmentHistory.push(historicalAckReport);					
				}
			})
		})


		forEach(remainingPeriods, period => {
			period.users = filter(period.users, u => filter(u.acknowledgments, ack => ack.acknowledged === ACKNOWLEDGEMENT_STATUS.NOT_ACKNOWLEDGED).length > 0)
			period.users = sortBy(period.users, ['firstName', 'lastName']);
		})

	}

	return report.periods;
}

/*
function loadTestData() : IMissedAcknowledgmentPeriod[] {
	const result : IMissedAcknowledgmentPeriod[] = [{
			"startDate": new Date("2023-08-20T06:00:00.000Z"),
			"endDate": new Date("2023-08-27T05:59:59.990Z"),
			"users": [{
				"userID": 23760,
				"firstName": "David",
				"lastName": "Owens",
				"acknowledgments": [],
				"acknowledgmentHistory": [{status: ACKNOWLEDGEMENT_STATUS.NOT_ACKNOWLEDGED}, {status: ACKNOWLEDGEMENT_STATUS.ACKNOWLEDGED}, {status: ACKNOWLEDGEMENT_STATUS.ACK_NOT_REQUIRED}, {status: ACKNOWLEDGEMENT_STATUS.ACKNOWLEDGED}, {status: ACKNOWLEDGEMENT_STATUS.NOT_ACKNOWLEDGED}, {status: ACKNOWLEDGEMENT_STATUS.ACKNOWLEDGED}]
			}, {
				"userID": 20354,
				"firstName": "Todd",
				"lastName": "Taylor",
				"acknowledgments": [],
				"acknowledgmentHistory": [{status: ACKNOWLEDGEMENT_STATUS.NOT_ACKNOWLEDGED}, {status: ACKNOWLEDGEMENT_STATUS.ACKNOWLEDGED}, {status: ACKNOWLEDGEMENT_STATUS.NOT_ACKNOWLEDGED}, {status: ACKNOWLEDGEMENT_STATUS.ACKNOWLEDGED}, {status: ACKNOWLEDGEMENT_STATUS.NOT_ACKNOWLEDGED}, {status: ACKNOWLEDGEMENT_STATUS.ACKNOWLEDGED}]
			}, {
				"userID": 15365,
				"firstName": "Christopher",
				"lastName": "Waddell",
				"acknowledgments": [],
				"acknowledgmentHistory": [{status: ACKNOWLEDGEMENT_STATUS.NOT_ACKNOWLEDGED}, {status: ACKNOWLEDGEMENT_STATUS.ACKNOWLEDGED}, {status: ACKNOWLEDGEMENT_STATUS.NOT_ACKNOWLEDGED}, {status: ACKNOWLEDGEMENT_STATUS.ACK_NOT_REQUIRED}, {status: ACKNOWLEDGEMENT_STATUS.NOT_ACKNOWLEDGED}, {status: ACKNOWLEDGEMENT_STATUS.ACKNOWLEDGED}]
			}, {
				"userID": 23058,
				"firstName": "William",
				"lastName": "Dickey",
				"acknowledgments": [],
				"acknowledgmentHistory": [{status: ACKNOWLEDGEMENT_STATUS.NOT_ACKNOWLEDGED}, {status: ACKNOWLEDGEMENT_STATUS.ACKNOWLEDGED}, {status: ACKNOWLEDGEMENT_STATUS.NOT_ACKNOWLEDGED}, {status: ACKNOWLEDGEMENT_STATUS.ACKNOWLEDGED}, {status: ACKNOWLEDGEMENT_STATUS.NOT_ACKNOWLEDGED}, {status: ACKNOWLEDGEMENT_STATUS.ACK_NOT_REQUIRED}]
			}, {
				"userID": 15879,
				"firstName": "Robert",
				"lastName": "Frost",
				"acknowledgments": [],
				"acknowledgmentHistory": [{status: ACKNOWLEDGEMENT_STATUS.NOT_ACKNOWLEDGED}, {status: ACKNOWLEDGEMENT_STATUS.ACKNOWLEDGED}, {status: ACKNOWLEDGEMENT_STATUS.NOT_ACKNOWLEDGED}, {status: ACKNOWLEDGEMENT_STATUS.ACKNOWLEDGED}, {status: ACKNOWLEDGEMENT_STATUS.NOT_ACKNOWLEDGED}, {status: ACKNOWLEDGEMENT_STATUS.ACKNOWLEDGED}]
			}],
			"isCurrent": true
		}, {
			"startDate": new Date("2023-08-13T06:00:00.000Z"),
			"endDate": new Date("2023-08-20T05:59:59.990Z"),
			"users": [{
			"userID": 23760,
			"firstName": "David",
			"lastName": "Owens",
			"acknowledgments": [],
			"acknowledgmentHistory": []
			}],
			"isCurrent": false
		}, {
			"startDate": new Date("2023-08-06T06:00:00.000Z"),
			"endDate": new Date("2023-08-13T05:59:59.990Z"),
			"users": [],
			"isCurrent": false
		}, {
			"startDate": new Date("2023-07-30T06:00:00.000Z"),
			"endDate": new Date("2023-08-06T05:59:59.990Z"),
			"users": [],
			"isCurrent": false
		}, {
			"startDate": new Date("2023-07-23T06:00:00.000Z"),
			"endDate": new Date("2023-07-30T05:59:59.990Z"),
			"users": [],
			"isCurrent": false
		}, {
			"startDate": new Date("2023-07-16T06:00:00.000Z"),
			"endDate": new Date("2023-07-23T05:59:59.990Z"),
			"users": [],
			"isCurrent": false
		}]

	return result;
}
*/
